import { useState } from 'react'
import { AnswerRecap } from '@/components/audition/answer-recap'
import { LaneSteps } from '@/components/audition/lane-steps'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import type { AuditionAnswers } from '@/lib/audition/answers'
import type { Lane } from '@/lib/audition/lane-steps'
import { formatPrice } from '@/lib/stripe/format-price'
import { trpc } from '@/lib/trpc'

/**
 * The page after the last wizard step (LG-1033): one honest line, the
 * answers on request, and two doors — "Hold your spot" (pay the starter
 * deposit now) or "Talk to us first" (book a call, pay later). Each door
 * opens the same three-step accordion, `LaneSteps`, with the committing
 * step last.
 *
 * Deliberately asymmetric: the paid door is the primary button and the
 * call door an outline. That asymmetry IS the spec's §1.3 presentation
 * guardrail — a peer button would change the lane mix the admin dashboard
 * reports on, which is how we'd notice.
 *
 * Mounted only behind `enable-fast-lane` (the caller gates), so `listPacks`
 * never spends its token-verify + DB read + Clerk call on a visitor who
 * will not see a price.
 */
export function LaneChoice({
  token,
  answers,
}: {
  token: string
  answers: AuditionAnswers
}) {
  const [lane, setLane] = useState<Lane | null>(null)
  const packs = trpc.audition.listPacks.useQuery({ token })
  const starter = packs.data?.starter ?? null

  if (lane) {
    return (
      <LaneSteps
        lane={lane}
        token={token}
        starter={starter}
        onBack={() => setLane(null)}
      />
    )
  }

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Application received 🎉</h1>
        <p className="text-muted-foreground">
          Your answers are saved to your account. Pick how you'd like to go from
          here.
        </p>
      </div>

      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button type="button" variant="link" className="px-0">
            Review my answers
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AnswerRecap answers={answers} />
        </CollapsibleContent>
      </Collapsible>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3 rounded-md border p-4">
          <h2 className="text-lg font-semibold">Hold your spot</h2>
          <p className="text-muted-foreground text-sm">
            {packs.isError
              ? // Without this the generic deposit copy sits above a dead
                // button with nothing said and no way back.
                "We couldn't load enrollment right now."
              : packs.isSuccess && !starter
                ? "Enrollment isn't available right now — book a call instead."
                : starter
                  ? `A ${formatPrice(starter.unitAmount, starter.currency)} deposit holds a place. It stays fully refundable until we decide. If we think your application is a good fit, it becomes your first month's payment.`
                  : 'A deposit holds a place. It stays fully refundable until we decide.'}
          </p>
          {packs.isError ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => void packs.refetch()}
            >
              Try again
            </Button>
          ) : (
            <Button
              type="button"
              className="w-full"
              disabled={!starter}
              onClick={() => setLane('hold')}
            >
              Hold my spot
            </Button>
          )}
        </div>
        <div className="space-y-3 rounded-md border p-4">
          <h2 className="text-lg font-semibold">Talk to us first</h2>
          <p className="text-muted-foreground text-sm">
            Not sure yet? Book a call, ask whatever you want, and decide
            afterwards. No deposit today.
          </p>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setLane('talk')}
          >
            Book a call
          </Button>
        </div>
      </div>
    </section>
  )
}
